/**
 * The report step, always the chain's last (report.v1, admin.section) — the
 * report's console (#1230, specs/facilitator-console.md → The report step).
 * Its runs are the module's actions in the section's shelf (create, images,
 * summary, move to results); this panel holds the facts and the decisions: the
 * material beyond what the actions make (with the script where there is no UI),
 * the translations into any language (#1442), the report settings, the excluded
 * inputs, and the ways to the report and its history.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, History } from 'lucide-react';
import useInstanceRecord from '../../../hooks/useInstanceRecord';
import useInstanceOverview from '../../../hooks/useInstanceOverview';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import ReportStatusPanel from '../../ReportAdmin/ReportStatusPanel';
import ReportTranslationsPanel from '../../ReportAdmin/ReportTranslationsPanel';
import ReportSettingsPanel from '../../ReportAdmin/ReportSettingsPanel';
import ExcludedInputsPanel from '../../InputExclusions/ExcludedInputsPanel.jsx';
import { Card, Facts, Actions, ActionLink, Muted, ErrorText } from './moduleSectionStyles';

const RESULTS_STATUSES = ['post-workshop', 'archived'];

export default function ReportStepContent({ instanceId }) {
  const { t, language } = useTranslation();
  const { instance } = useInstanceRecord(instanceId);
  const { overview, error } = useInstanceOverview(instanceId);
  const inResults = RESULTS_STATUSES.includes(instance?.status);

  return (
    <Card>
      <Muted>{t('reportStep.intro')}</Muted>
      {error && <ErrorText>{t('reportStep.loadFailed', { error })}</ErrorText>}
      {overview && (
        <Facts>
          <dt>{t('reportStep.created')}</dt>
          <dd>
            {overview.reports > 0 && overview.report_generated_at
              ? new Date(overview.report_generated_at).toLocaleString(language)
              : t('reportStep.notCreated')}
          </dd>
          <dt>{t('reportStep.stage')}</dt>
          <dd>{inResults ? t('reportStep.inResults') : t('reportStep.notInResults')}</dd>
        </Facts>
      )}
      <Actions>
        <ActionLink as="a" href={`/raportti?instance=${instanceId}`} target="_blank" rel="noopener noreferrer" $primary={inResults}>
          <FileText size={16} /> {t('reportStep.openReport')}
        </ActionLink>
        <ActionLink as={Link} to={`/reportgen?instance=${instanceId}`}>
          <History size={16} /> {t('reportStep.history')}
        </ActionLink>
      </Actions>
      <ReportStatusPanel instanceId={instanceId} />
      <ReportTranslationsPanel instanceId={instanceId} instanceLanguage={instance?.language} />
      <ReportSettingsPanel instanceId={instanceId} />
      <ExcludedInputsPanel instanceId={instanceId} />
    </Card>
  );
}
